import { Divider, InputAdornment, TextField } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useMutation } from "react-query";
import { KTCard } from "../../_metronic/helpers";
import {
  addAppConfigSetting,
  getChatImageCompressionQuality,
  getChatVideoCompressionQuality,
  getChatVideoMaxSize,
  getDefaultCommission,
  getRefundRequestWindowDays,
  getYoutubeUrl,
  updateChatImageCompressionQuality,
  updateChatVideoCompressionQuality,
  updateChatVideoMaxSize,
  updateDefaultCommission,
  updateRefundRequestWindowDays,
  updateYoutubeUrl,
} from "../../api";
import { snackActions } from "../../helpers/SnackUtilsConfigurator";

// import AddEditUser from "./AddEditUser";

const CHAT_IMAGE_COMPRESSION_QUALITY_DESCRIPTION =
  "Controls the image compression quality for chat image uploads. Enter a value between 0 and 1, where 1 keeps the highest image quality with less compression and lower values apply more compression to reduce image size.";

const CHAT_VIDEO_COMPRESSION_QUALITY_DESCRIPTION =
  "Controls the video compression quality for chat video uploads. Enter a value between 0 and 1, where 1 keeps the highest video quality with less compression and lower values apply more compression to reduce video size.";

const CHAT_VIDEO_MAX_SIZE_DESCRIPTION =
  "Defines the maximum allowed file size for chat video uploads in MB. For example, 50 allows users to upload videos up to 50 MB. Videos larger than this limit will not be allowed to upload.";

const REFUND_REQUEST_WINDOW_DAYS_DESCRIPTION =
  "Defines the number of days from the payment date during which a user can request a refund. For example, 30 allows users to submit a refund request within 30 days of payment.";

type AppConfigPayload = {
  id: number | string;
  key: string;
  value: string;
  description: string | null;
};

const getSettingRecord = (settingData: any) => settingData?.result;

const createSettingPayload = (
  settingData: any,
  key: string,
  value: string,
  description: string | null,
): AppConfigPayload => ({
  id: getSettingRecord(settingData)?.id ?? 0,
  key,
  value,
  description,
});

const setSettingStateValue = (
  settingData: any,
  setValue: React.Dispatch<React.SetStateAction<string>>,
) => {
  const value = getSettingRecord(settingData)?.value;

  if (value !== undefined && value !== null) {
    setValue(String(value));
  }
};

const AppConfig: React.FC = () => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [commission, setCommission] = useState("");
  const [chatImageCompressionQuality, setChatImageCompressionQuality] =
    useState("");
  const [chatVideoCompressionQuality, setChatVideoCompressionQuality] =
    useState("");
  const [chatVideoMaxSize, setChatVideoMaxSize] = useState("");
  const [refundRequestWindowDays, setRefundRequestWindowDays] = useState("");
  const [commissionError, setCommissionError] = useState("");
  const [
    chatImageCompressionQualityError,
    setChatImageCompressionQualityError,
  ] = useState("");
  const [
    chatVideoCompressionQualityError,
    setChatVideoCompressionQualityError,
  ] = useState("");
  const [chatVideoMaxSizeError, setChatVideoMaxSizeError] = useState("");
  const [refundRequestWindowDaysError, setRefundRequestWindowDaysError] =
    useState("");

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setYoutubeUrl(event.target.value);
  };

  const handleQualityChange = (
    value: string,
    label: string,
    setValue: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    if (value === "") {
      setValue("");
      setError(`${label} is required.`);
      return;
    }

    if (value.includes("%")) {
      return;
    }

    const regex = /^(0(\.\d*)?|1(\.0*)?)$/;

    if (!regex.test(value)) {
      setError(`${label} must be a number between 0 and 1.`);
      return;
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue) || numValue < 0 || numValue > 1) {
      setError(`${label} must be a number between 0 and 1.`);
      return;
    }

    setValue(value);
    setError("");
  };

  const handlePositiveIntegerChange = (
    value: string,
    label: string,
    setValue: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    if (value === "") {
      setValue("");
      setError(`${label} is required.`);
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    const numValue = Number(value);

    if (numValue <= 0) {
      setValue(value);
      setError(`${label} must be greater than 0.`);
      return;
    }

    setValue(value);
    setError("");
  };

  const handleCommissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    //Allow clearing (temporary empty) but mark as invalid
    if (value === "") {
      setCommission("");
      setCommissionError("Commission is required.");
      return;
    }

    //Block % or any non-numeric input (we use regex later, but early block helps)
    if (value.includes("%")) {
      return; // Ignore
    }

    //Regex: Max 3 digits before decimal, max 2 after
    const regex = /^(?!0\d)\d{1,3}(\.\d{1,2})?$/;

    const partialRegex = /^\d{1,3}\.$/;

    if (!regex.test(value) && !partialRegex.test(value)) {
      return; // Reject invalid format
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      setCommissionError("Invalid number");
      return;
    }

    //Final Validation
    if (numValue <= 0) {
      setCommissionError("Commission must be greater than 0.");
      return;
    }

    if (numValue > 20.0) {
      setCommissionError("Commission cannot exceed 20.0");
      return;
    }

    //All valid
    setCommission(value);
    setCommissionError("");
  };

  const {
    mutateAsync: getYoutubeUrlData,
    data: YoutubeUrlData,
    isLoading: isGettingYoutubeUrl,
  } = useMutation(getYoutubeUrl);

  const {
    mutateAsync: getCommissionData,
    data: CommissionData,
    isLoading: isGettingCommission,
  } = useMutation(getDefaultCommission);

  const {
    mutateAsync: getChatImageCompressionQualityData,
    data: ChatImageCompressionQualityData,
    isLoading: isGettingChatImageCompressionQuality,
  } = useMutation(getChatImageCompressionQuality);

  const {
    mutateAsync: getChatVideoCompressionQualityData,
    data: ChatVideoCompressionQualityData,
    isLoading: isGettingChatVideoCompressionQuality,
  } = useMutation(getChatVideoCompressionQuality);

  const {
    mutateAsync: getChatVideoMaxSizeData,
    data: ChatVideoMaxSizeData,
    isLoading: isGettingChatVideoMaxSize,
  } = useMutation(getChatVideoMaxSize);

  const {
    mutateAsync: getRefundRequestWindowDaysData,
    data: RefundRequestWindowDaysData,
    isLoading: isGettingRefundRequestWindowDays,
  } = useMutation(getRefundRequestWindowDays);

  const { mutateAsync: addAppConfig, isLoading: isAddingAppConfig } =
    useMutation("add-app-config", addAppConfigSetting);

  const { mutateAsync: updateUrl, isLoading: isUpdatingUrl } = useMutation(
    "update-url",
    updateYoutubeUrl,
  );

  const { mutateAsync: updateCommission, isLoading: isUpdatingCommission } =
    useMutation("update-commission", updateDefaultCommission);

  const {
    mutateAsync: updateChatImageCompression,
    isLoading: isUpdatingChatImageCompression,
  } = useMutation(
    "update-chat-image-compression",
    updateChatImageCompressionQuality,
  );

  const {
    mutateAsync: updateChatVideoCompression,
    isLoading: isUpdatingChatVideoCompression,
  } = useMutation(
    "update-chat-video-compression",
    updateChatVideoCompressionQuality,
  );

  const { mutateAsync: updateVideoMaxSize, isLoading: isUpdatingVideoMaxSize } =
    useMutation("update-chat-video-max-size", updateChatVideoMaxSize);

  const {
    mutateAsync: updateRefundWindowDays,
    isLoading: isUpdatingRefundWindowDays,
  } = useMutation(
    "update-refund-request-window-days",
    updateRefundRequestWindowDays,
  );

  const isGettingConfig =
    isGettingYoutubeUrl ||
    isGettingCommission ||
    isGettingChatImageCompressionQuality ||
    isGettingChatVideoCompressionQuality ||
    isGettingChatVideoMaxSize ||
    isGettingRefundRequestWindowDays;

  const isUpdating =
    isGettingConfig ||
    isAddingAppConfig ||
    isUpdatingUrl ||
    isUpdatingCommission ||
    isUpdatingChatImageCompression ||
    isUpdatingChatVideoCompression ||
    isUpdatingVideoMaxSize ||
    isUpdatingRefundWindowDays;

  const hasValidationError =
    !!commissionError ||
    !!chatImageCompressionQualityError ||
    !!chatVideoCompressionQualityError ||
    !!chatVideoMaxSizeError ||
    !!refundRequestWindowDaysError;

  const fetchAppConfig = useCallback(async () => {
    await Promise.all([
      getYoutubeUrlData({}),
      getCommissionData({}),
      getChatImageCompressionQualityData({}),
      getChatVideoCompressionQualityData({}),
      getChatVideoMaxSizeData({}),
      getRefundRequestWindowDaysData({}),
    ]);
  }, [
    getChatImageCompressionQualityData,
    getChatVideoCompressionQualityData,
    getChatVideoMaxSizeData,
    getCommissionData,
    getRefundRequestWindowDaysData,
    getYoutubeUrlData,
  ]);

  const saveSetting = useCallback(
    async (
      settingData: any,
      payload: AppConfigPayload,
      updateSetting: (payloadData: AppConfigPayload) => Promise<any>,
    ) => {
      if (getSettingRecord(settingData)) {
        return updateSetting(payload);
      }

      if (settingData !== undefined) {
        return addAppConfig(payload);
      }

      return {
        result: false,
        message: "Unable to load app config setting. Please try again.",
      };
    },
    [addAppConfig],
  );

  const handleSubmit = useCallback(async () => {
    if (!chatImageCompressionQuality) {
      setChatImageCompressionQualityError(
        "Chat image compression quality is required.",
      );
    }

    if (!chatVideoCompressionQuality) {
      setChatVideoCompressionQualityError(
        "Chat video compression quality is required.",
      );
    }

    if (!chatVideoMaxSize) {
      setChatVideoMaxSizeError("Chat video max size is required.");
    }

    if (!refundRequestWindowDays) {
      setRefundRequestWindowDaysError(
        "Refund request window is required.",
      );
    }

    if (hasValidationError) {
      snackActions.error("Please fix the highlighted app config fields.");
      return;
    }

    if (
      !chatImageCompressionQuality ||
      !chatVideoCompressionQuality ||
      !chatVideoMaxSize ||
      !refundRequestWindowDays
    ) {
      snackActions.error("Please fill all app config fields.");
      return;
    }

    const bodyParams = createSettingPayload(
      YoutubeUrlData,
      "Youtube_URL",
      youtubeUrl,
      null,
    );

    const updateCommissionPayload = createSettingPayload(
      CommissionData,
      "Default_PlatformFee_Percentage",
      commission,
      null,
    );

    const updateChatImageCompressionPayload = createSettingPayload(
      ChatImageCompressionQualityData,
      "CHAT_IMAGE_COMPRESSION_QUALITY",
      chatImageCompressionQuality,
      CHAT_IMAGE_COMPRESSION_QUALITY_DESCRIPTION,
    );

    const updateChatVideoCompressionPayload = createSettingPayload(
      ChatVideoCompressionQualityData,
      "CHAT_VIDEO_COMPRESSION_QUALITY",
      chatVideoCompressionQuality,
      CHAT_VIDEO_COMPRESSION_QUALITY_DESCRIPTION,
    );

    const updateVideoMaxSizePayload = createSettingPayload(
      ChatVideoMaxSizeData,
      "CHAT_VIDEO_MAX_SIZE",
      chatVideoMaxSize,
      CHAT_VIDEO_MAX_SIZE_DESCRIPTION,
    );

    const updateRefundWindowDaysPayload = createSettingPayload(
      RefundRequestWindowDaysData,
      "REFUND_REQUEST_WINDOW_DAYS",
      refundRequestWindowDays,
      REFUND_REQUEST_WINDOW_DAYS_DESCRIPTION,
    );

    const [
      response,
      commissionResponse,
      chatImageCompressionResponse,
      chatVideoCompressionResponse,
      videoMaxSizeResponse,
      refundWindowDaysResponse,
    ] = await Promise.all([
      saveSetting(YoutubeUrlData, bodyParams, updateUrl),
      saveSetting(CommissionData, updateCommissionPayload, updateCommission),
      saveSetting(
        ChatImageCompressionQualityData,
        updateChatImageCompressionPayload,
        updateChatImageCompression,
      ),
      saveSetting(
        ChatVideoCompressionQualityData,
        updateChatVideoCompressionPayload,
        updateChatVideoCompression,
      ),
      saveSetting(
        ChatVideoMaxSizeData,
        updateVideoMaxSizePayload,
        updateVideoMaxSize,
      ),
      saveSetting(
        RefundRequestWindowDaysData,
        updateRefundWindowDaysPayload,
        updateRefundWindowDays,
      ),
    ]);

    if (
      response?.result &&
      commissionResponse?.result &&
      chatImageCompressionResponse?.result &&
      chatVideoCompressionResponse?.result &&
      videoMaxSizeResponse?.result &&
      refundWindowDaysResponse?.result
    ) {
      await fetchAppConfig();
      snackActions.success("App Config Saved Successfully!");
    } else {
      const failedResponse = [
        response,
        commissionResponse,
        chatImageCompressionResponse,
        chatVideoCompressionResponse,
        videoMaxSizeResponse,
        refundWindowDaysResponse,
      ].find((settingResponse) => !settingResponse?.result);

      snackActions.error(failedResponse?.message ?? "Something went wrong!");
    }
  }, [
    ChatImageCompressionQualityData,
    ChatVideoCompressionQualityData,
    ChatVideoMaxSizeData,
    CommissionData,
    RefundRequestWindowDaysData,
    YoutubeUrlData,
    chatImageCompressionQuality,
    chatVideoCompressionQuality,
    chatVideoMaxSize,
    commission,
    fetchAppConfig,
    hasValidationError,
    refundRequestWindowDays,
    saveSetting,
    updateChatImageCompression,
    updateChatVideoCompression,
    updateCommission,
    updateRefundWindowDays,
    updateUrl,
    updateVideoMaxSize,
    youtubeUrl,
  ]);

  useEffect(() => {
    fetchAppConfig();
  }, [fetchAppConfig]);

  useEffect(() => {
    setSettingStateValue(YoutubeUrlData, setYoutubeUrl);
    setSettingStateValue(CommissionData, setCommission);
    setSettingStateValue(
      ChatImageCompressionQualityData,
      setChatImageCompressionQuality,
    );
    setSettingStateValue(
      ChatVideoCompressionQualityData,
      setChatVideoCompressionQuality,
    );
    setSettingStateValue(ChatVideoMaxSizeData, setChatVideoMaxSize);
    setSettingStateValue(
      RefundRequestWindowDaysData,
      setRefundRequestWindowDays,
    );
    console.log("YoutubeUrlData", YoutubeUrlData);
    console.log("CommissionData", CommissionData);
  }, [
    ChatImageCompressionQualityData,
    ChatVideoCompressionQualityData,
    ChatVideoMaxSizeData,
    CommissionData,
    RefundRequestWindowDaysData,
    YoutubeUrlData,
  ]);

  return (
    <Card className="p-6">
      <>
        <KTCard>
          <h3 style={{ position: "absolute", top: "5px" }}>
            Add / Update App Config :
          </h3>

          <div style={{ marginTop: "50px", display: "flex", gap: "10px" }}>
            <TextField
              label="Enter YouTube URL"
              variant="outlined"
              fullWidth
              value={youtubeUrl}
              onChange={handleUrlChange}
            />
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "10px",
            }}
          >
            <TextField
              label="Enter Default Commission"
              variant="outlined"
              fullWidth
              value={commission}
              onChange={handleCommissionChange}
              error={!!commissionError} // Highlights the field in red if there's an error
              helperText={
                commissionError ||
                "Enter a value greater than 0 and up to 20.00"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span style={{ fontWeight: 500, color: "#555" }}>%</span>
                  </InputAdornment>
                ),
              }}
              type="text" // Use "text" to have full control over formatting
              inputProps={{
                inputMode: "decimal", // Shows numeric keypad on mobile
                "aria-label": "Commission input",
              }}
            />
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "10px",
            }}
          >
            <TextField
              label="Chat Image Compression Quality"
              variant="outlined"
              fullWidth
              value={chatImageCompressionQuality}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleQualityChange(
                  event.target.value,
                  "Chat image compression quality",
                  setChatImageCompressionQuality,
                  setChatImageCompressionQualityError,
                )
              }
              error={!!chatImageCompressionQualityError}
              helperText={
                chatImageCompressionQualityError ||
                CHAT_IMAGE_COMPRESSION_QUALITY_DESCRIPTION
              }
              type="text"
              inputProps={{
                inputMode: "decimal",
                "aria-label": "Chat image compression quality input",
              }}
            />
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "10px",
            }}
          >
            <TextField
              label="Chat Video Compression Quality"
              variant="outlined"
              fullWidth
              value={chatVideoCompressionQuality}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleQualityChange(
                  event.target.value,
                  "Chat video compression quality",
                  setChatVideoCompressionQuality,
                  setChatVideoCompressionQualityError,
                )
              }
              error={!!chatVideoCompressionQualityError}
              helperText={
                chatVideoCompressionQualityError ||
                CHAT_VIDEO_COMPRESSION_QUALITY_DESCRIPTION
              }
              type="text"
              inputProps={{
                inputMode: "decimal",
                "aria-label": "Chat video compression quality input",
              }}
            />
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "10px",
            }}
          >
            <TextField
              label="Chat Video Max Size"
              variant="outlined"
              fullWidth
              value={chatVideoMaxSize}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handlePositiveIntegerChange(
                  event.target.value,
                  "Chat video max size",
                  setChatVideoMaxSize,
                  setChatVideoMaxSizeError,
                )
              }
              error={!!chatVideoMaxSizeError}
              helperText={
                chatVideoMaxSizeError || CHAT_VIDEO_MAX_SIZE_DESCRIPTION
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontWeight: 500, color: "#555" }}>MB</span>
                  </InputAdornment>
                ),
              }}
              type="text"
              inputProps={{
                inputMode: "numeric",
                "aria-label": "Chat video max size input",
              }}
            />
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "10px",
            }}
          >
            <TextField
              label="Refund Request Window"
              variant="outlined"
              fullWidth
              value={refundRequestWindowDays}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handlePositiveIntegerChange(
                  event.target.value,
                  "Refund request window",
                  setRefundRequestWindowDays,
                  setRefundRequestWindowDaysError,
                )
              }
              error={!!refundRequestWindowDaysError}
              helperText={
                refundRequestWindowDaysError ||
                REFUND_REQUEST_WINDOW_DAYS_DESCRIPTION
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontWeight: 500, color: "#555" }}>
                      days
                    </span>
                  </InputAdornment>
                ),
              }}
              type="text"
              inputProps={{
                inputMode: "numeric",
                "aria-label": "Refund request window days input",
              }}
            />
          </div>

          <div className="d-flex justify-content-between">
            <Button
              disabled={isUpdating || hasValidationError}
              variant="success"
              style={{
                width: "100px",
                marginLeft: "auto",
                marginBottom: "10px",
                marginTop: "25px",
              }}
              onClick={handleSubmit}
            >
              {isUpdating ? "Loading.." : "Submit"}
            </Button>
          </div>

          <Divider style={{ marginBottom: "20px", marginTop: "20px" }} />
        </KTCard>
      </>
    </Card>
  );
};

export default AppConfig;
